import { DEG } from '../../core/math';
import { resolvePalette } from '../../core/palette';
import { contoursToPath2D } from '../../core/text/flatten';
import { createCanvas, get2d } from '../../core/text/rasterize';
import type { Ctx2D, RenderTarget, TechniqueContext } from '../types';
import type { GlitchValues } from './params';

/**
 * Glitch.
 *
 * Unlike the field-driven techniques this one is inherently raster: it renders the
 * glyphs into its own buffer and then damages that buffer. A technique cannot see the
 * layers beneath it — by design — so it brings its own source rather than pretending to
 * glitch the composition.
 *
 * The passes run in the order a real signal path would break: slice displacement first
 * (transport), then channel separation (optics), then sorting and block quantisation
 * (codec).
 */
export interface GlitchState {
  buffer: OffscreenCanvas | HTMLCanvasElement;
  bctx: Ctx2D;
  built: boolean;
}

function values(ctx: TechniqueContext): GlitchValues {
  return ctx.values as unknown as GlitchValues;
}

export function init(ctx: TechniqueContext): GlitchState {
  const buffer = createCanvas(ctx.width, ctx.height);
  return { buffer, bctx: get2d(buffer), built: false };
}

export function step(state: GlitchState, _dt: number, ctx: TechniqueContext): GlitchState {
  if (state.built) return state;
  const v = values(ctx);
  const { width, height } = ctx;
  const c = state.bctx;
  const palette = resolvePalette(v.palette, ctx.rng.fork(1));

  c.setTransform(1, 0, 0, 1, 0, 0);
  c.clearRect(0, 0, width, height);

  // ── Source: the glyphs, coloured from the palette ────────────────────────
  const path = contoursToPath2D(ctx.field.contours);
  if (v.colorMode === 'gradient') {
    const bounds = ctx.field.bounds;
    const gradient = c.createLinearGradient(bounds.x, bounds.y, bounds.x + bounds.width, bounds.y + bounds.height);
    for (let i = 0; i <= 8; i++) gradient.addColorStop(i / 8, palette.at(i / 8));
    c.fillStyle = gradient;
  } else {
    c.fillStyle = palette.stop(0);
  }
  c.fill(path, 'nonzero');

  // ── Slice displacement ───────────────────────────────────────────────────
  if (v.shift > 0 && v.slices > 0) {
    const source = createCanvas(width, height);
    const sctx = get2d(source);
    sctx.drawImage(state.buffer as CanvasImageSource, 0, 0);

    for (let i = 0; i < v.slices; i++) {
      if (!ctx.rng.chance(v.probability)) continue;
      const h = ctx.rng.range(v.sliceHeight[0], v.sliceHeight[1]);
      const y = ctx.rng.next() * height;
      const dx = ctx.rng.signed(v.shift);

      for (let k = 0; k <= v.repeat; k++) {
        const offset = dx * (1 + k * 0.6);
        c.drawImage(source as CanvasImageSource, 0, y, width, h, offset, y, width, h);
      }
    }
  }

  // ── RGB split ────────────────────────────────────────────────────────────
  if (v.rgbSplit > 0) {
    const angle = v.rgbAngle * DEG;
    const ox = Math.cos(angle) * v.rgbSplit;
    const oy = Math.sin(angle) * v.rgbSplit;
    const image = c.getImageData(0, 0, width, height);
    const src = new Uint8ClampedArray(image.data);
    const d = image.data;

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const i = (y * width + x) * 4;
        const rx = Math.min(width - 1, Math.max(0, Math.round(x + ox)));
        const ry = Math.min(height - 1, Math.max(0, Math.round(y + oy)));
        const bx = Math.min(width - 1, Math.max(0, Math.round(x - ox)));
        const by = Math.min(height - 1, Math.max(0, Math.round(y - oy)));
        const ri = (ry * width + rx) * 4;
        const bi = (by * width + bx) * 4;
        d[i] = src[ri];
        d[i + 2] = src[bi + 2];
        // Alpha follows the widest of the three samples, or the split would eat the
        // edges of the letters instead of fringing them.
        d[i + 3] = Math.max(src[i + 3], src[ri + 3], src[bi + 3]);
      }
    }
    c.putImageData(image, 0, 0);
  }

  // ── Pixel sorting ────────────────────────────────────────────────────────
  if (v.pixelSort) {
    const image = c.getImageData(0, 0, width, height);
    sortRuns(image, width, height, v.sortThreshold, v.sortDirection === 'vertical');
    c.putImageData(image, 0, 0);
  }

  // ── Block quantisation ───────────────────────────────────────────────────
  if (v.blockNoise > 0) {
    const image = c.getImageData(0, 0, width, height);
    const d = image.data;
    const block = 8;
    const levels = Math.max(2, Math.round(32 - v.blockNoise * 28));
    const step255 = 255 / (levels - 1);

    for (let by = 0; by < height; by += block) {
      for (let bx = 0; bx < width; bx += block) {
        let r = 0;
        let g = 0;
        let b = 0;
        let a = 0;
        let n = 0;
        for (let y = by; y < Math.min(height, by + block); y++) {
          for (let x = bx; x < Math.min(width, bx + block); x++) {
            const i = (y * width + x) * 4;
            r += d[i];
            g += d[i + 1];
            b += d[i + 2];
            a += d[i + 3];
            n++;
          }
        }
        if (n === 0) continue;
        // The DC term of the block, quantised — the visible part of JPEG damage.
        const qr = Math.round(r / n / step255) * step255;
        const qg = Math.round(g / n / step255) * step255;
        const qb = Math.round(b / n / step255) * step255;
        const qa = a / n;
        for (let y = by; y < Math.min(height, by + block); y++) {
          for (let x = bx; x < Math.min(width, bx + block); x++) {
            const i = (y * width + x) * 4;
            d[i] += (qr - d[i]) * v.blockNoise;
            d[i + 1] += (qg - d[i + 1]) * v.blockNoise;
            d[i + 2] += (qb - d[i + 2]) * v.blockNoise;
            d[i + 3] += (qa - d[i + 3]) * v.blockNoise;
          }
        }
      }
    }
    c.putImageData(image, 0, 0);
  }

  state.built = true;
  return state;
}

/**
 * Sorts contiguous runs of bright pixels by luminance.
 *
 * Only runs above the threshold are sorted, which is what makes pixel sorting read as
 * a deliberate smear rather than uniform mush: the dark background stays put and the
 * bright letterforms stretch.
 */
function sortRuns(image: ImageData, width: number, height: number, threshold: number, vertical: boolean): void {
  const d = image.data;
  const cut = threshold * 255;
  const outer = vertical ? width : height;
  const inner = vertical ? height : width;

  const luminance = (i: number): number => d[i] * 0.299 + d[i + 1] * 0.587 + d[i + 2] * 0.114;
  const indexAt = (o: number, k: number): number => (vertical ? (k * width + o) * 4 : (o * width + k) * 4);

  for (let o = 0; o < outer; o++) {
    let runStart = -1;
    for (let k = 0; k <= inner; k++) {
      const i = k < inner ? indexAt(o, k) : -1;
      const bright = i >= 0 && d[i + 3] > 8 && luminance(i) > cut;

      if (bright && runStart < 0) runStart = k;
      else if (!bright && runStart >= 0) {
        const length = k - runStart;
        if (length > 2) {
          const pixels: number[][] = [];
          for (let m = runStart; m < k; m++) {
            const idx = indexAt(o, m);
            pixels.push([d[idx], d[idx + 1], d[idx + 2], d[idx + 3]]);
          }
          pixels.sort((a, b) => a[0] * 0.299 + a[1] * 0.587 + a[2] * 0.114 - (b[0] * 0.299 + b[1] * 0.587 + b[2] * 0.114));
          for (let m = 0; m < pixels.length; m++) {
            const idx = indexAt(o, runStart + m);
            d[idx] = pixels[m][0];
            d[idx + 1] = pixels[m][1];
            d[idx + 2] = pixels[m][2];
            d[idx + 3] = pixels[m][3];
          }
        }
        runStart = -1;
      }
    }
  }
}

export function isComplete(state: GlitchState): boolean {
  return state.built;
}

export function draw(state: GlitchState, target: RenderTarget, ctx: TechniqueContext): void {
  const v = values(ctx);
  target.ctx.globalAlpha *= v.opacity;
  target.ctx.drawImage(state.buffer as CanvasImageSource, 0, 0, target.width, target.height);
}

export function elementCount(_state: GlitchState): number {
  return 0;
}
